import React, {useState, useEffect} from 'react'
import { Link, Router, navigate } from "@reach/router"

//data functions
import axios from 'axios'
import * as Sentry from "@sentry/react"
import QuoteFormReducer from "./reducers/quote-form";
import NavigationReducer from "./reducers/navigation"
import useGet from "../shared/hooks/use-get"
import usePost from "../shared/hooks/use-post"
import QuoteContext from "./quote-context"

//master layout
import Heading from './layout/heading'
import Progress from './layout/progress-bar'
import ErrorMessage from './layout/error'

//steps (do we need to look at lazy loading?)
import Itinerary from './steps/itinerary'
import Accommodation from './steps/accommodation/accommodation'
import AccommodationTypes from './steps/accommodation/accommodation-types'
import Activities from './steps/activities'
import SuperPackages from './steps/superpackages'
import Submit from './steps/submit'

//info window
import Modal from '../shared/modal'

//misc
import Loader from '../shared/loader'


const QuotebuilderV3 = ({user, destination, hasBookings}) => {

	//***************
	//*** STATES  ***
	//***************

	//is this user logged in?
	const[loggedIn, setLoggedIn] = useState(!!user);

	//custom hooks for REST	
	const[ loading, setLoading ] = useState( true )
	const[ data, setData ] = useState( null )
	
	
	
	//store a set of elements (act,hotel etc) for this location
	const[elements, setElements] = useState({
		accommodation: [],
		events: [],
		packages: [],
		siteTags: [],
		eventTags: [],
		hotelStandards: [],
		hotelStandardFilter: [],
		image: null
	})

	//has the user asked us to prepopulate the itinerary with a pre-selected activity/hotel
	const[selectedItem,  setSelectedItem] = useState(false);

	//*****************
	//*** REDUCERS  ***
	//*****************

	//reducer for the form data
	const[formData, formDispatcher] = QuoteFormReducer(  destination, loggedIn );

	//reducer for the navigation
	const[navigation, navigationDispatcher] = NavigationReducer(  destination.siteId, destination.id, destination.slug, destination.name, destination.forceHotel, destination.hasPackages, destination.hasSuperPackages, loggedIn );
	
	//*******************
	//*** NAVIGATION  ***
	//********************

	const getFullPath = ( path ) => {
		const basePath = destination.basePath != undefined ? destination.basePath : '/quote';
		if( path == null ) {
			return basePath + '/' + destination.slug;
		} else {
			return basePath + '/' + destination.slug + '/' + path;
		}
	}

	const steps = {
		itinerary: getFullPath( 'itinerary' ),
		activities: getFullPath( 'activities' ),
		accommodation: getFullPath( 'accommodation' ),
		accommodationTypes: getFullPath( 'accommodation-types' ),
		superPackages: getFullPath( 'superpackages' ),
		submit: getFullPath( 'submit' ),
	};


	//********************************
	//*** PERFORM ACTIONS ON LOAD  ***
	//********************************

	//get data on page load
	useEffect(() => {
		getData();
		navigationDispatcher({
			type: 'TRACK_EVENT',
			action: 'event',
			category: 'pageview',
			name: 'land-on-quote',
			trigger: 'web-js',
			data:  {
				id: destination.id,
				name: destination.name,
				slug: destination.slug
			}
		})
	}, [])
	
	
	async function getData() {
		
		setLoading( true )
		
		const url = '/quote/data/destination/' + destination.slug;
		
		try {
			
			const response = await axios.get( url )
			setData( response.data )

		} catch( error ) {
			
			Sentry.captureMessage("URL CALLED FAILED: "  + url );
			Sentry.setExtra('error', error)
			Sentry.captureException(error);
			
			//navigationDispatcher({type: "RAISE_ERROR", object: error });
			getDataBackup()

			navigationDispatcher({
				type: 'TRACK_EVENT',
				action: 'error',
				category: 'load-errors',
				name: 'index',
				trigger: 'web-js',
				data:  error
			});
			
		}
		finally {
			setLoading( false )
		}
	}
	
	async function getDataBackup() {
		
		setLoading( true )
		
		const url = '/quote/data/destination/' + destination.slug;
		
		try {
			
			const response = await axios.get( url )
			setData( response.data )

		} catch( error ) {
			
			Sentry.captureMessage("URL CALLED FAILED ON 2nd Attempt: "  + url );
			//Sentry.setExtra('error', error)
			//Sentry.captureException(error);
			
			navigationDispatcher({type: "RAISE_ERROR", object: error });

			navigationDispatcher({
				type: 'TRACK_EVENT',
				action: 'error',
				category: 'load-errors',
				name: 'index',
				trigger: 'web-js',
				data:  error
			});
			
		}
		finally {
			setLoading( false )
		}
	}


	//*********************
	//*** DATA CHANGES  ***
	//*********************
	

	//save successful data and preload any items.
	useEffect(()=>{
		if( data !== null ){
			if( typeof data !== "undefined" ){
				setElements(data);
				//Are we going to preload any activitites or hotels onto the quote?
				preloadedItems( destination.preSelectedItems );
			}
			if( typeof data.accommodation !== "undefined" ){
				navigation.hasHotels = data.accommodation.length > 0 ? true : false;
			}
		}
	},[ data ])

	
	//navigate to a page when step is updated
	useEffect(()=>{
		navigate( steps[navigation.step.path], { state: navigation.step.params } );
	},[ navigation.step ]);

	
	//scroll to top when error is detected
	useEffect(()=>{
		if( navigation.error.message ) {
			window.scrollTo({top: 138, behavior: 'smooth'})
		}
	},[navigation.error]);


	//***********************
	//*** MISC FUNCTIONS  ***
	//***********************

	//Should we preload their Itinerary with any items?
	const preloadedItems = (items) => {
		
		if( items.length > 0 ) {
			items.map((item) => {
				switch( item.type ) {
				case 'event':
					formDispatcher({type: "ADD_ACTIVITY", event:{
						id: item.data.id,
						day: 1,
						name: item.data.name,
						period: item.data.day ? 0 : 1,
						preferredTimeSlot: item.data.day ? 0 : 2,
						image:  item.data.image
					}})
					setSelectedItem( item.data );
					break;
				case 'hotel':
					formDispatcher({type: "ADD_HOTEL", hotel:{
						id: item.data.id,
						type: "named",
						name: item.data.name,
						day: 1,
						nights: item.nights,
						image: item.data.image
					}})
					setSelectedItem( item.data );
					break;
				case 'package':
					item.data.events.map((event) => {
						formDispatcher({type: "ADD_ACTIVITY", event:{
							id: event.id,
							day: 1,
							name: event.name,
							period: event.day ? 0 : 1,
							preferredTimeSlot: event.day ? 0 : 2,
							image:  event.image
						}})	
					})
					if( item.data.hotel ) {
						formDispatcher({type: "ADD_HOTEL", hotel:{
							id: item.data.hotel.id,
							type: "named",
							name: item.data.hotel.name,
							day: 1,
							nights: item.nights,
							image: item.data.hotel.image
						}})
					}
					
					setSelectedItem( item.data );
					
					formDispatcher({type: "ADD_PACKAGE", package: { id: item.data.id, name: item.data.name }})
							
					if( !item.data.hotel ) {
						navigationDispatcher({type: "GOTO_STEP", step: "accommodation", params: { day: 1, isPackage:true  } }) 
					} else {
						if( !navigation.loggedIn ) {
							navigationDispatcher({type: "GOTO_STEP", step: "submit", params: { day: 1, isPackage:true } })
						}
					}
					break;
				}
			});
		}
		/*
		
		//if set, and not already in basket
		if( eventId > 0 && !formData.activities.find( event => event.id == eventId )){
			let hasSelectedActivity = allItems.events.find( event => event.id == eventId );
			if( hasSelectedActivity ){
				formDispatcher({type: "ADD_ACTIVITY", event:{
					id: eventId,
					day: 1,
					name: hasSelectedActivity.name,
					period: hasSelectedActivity.day ? 0 : 1,
					preferredTimeSlot: hasSelectedActivity.day ? 0 : 2,
					image:  hasSelectedActivity.image
				}})
			}
			setSelectedItem( hasSelectedActivity );
		}
		if( hotelId > 0 && !formData.hotel ){
			let hasSelectedHotel = allItems.accommodation.find( hotel => hotel.id == hotelId );
			if( hasSelectedHotel ){
				formDispatcher({type: "ADD_HOTEL", hotel:{
					id: hotelId,
					type: "named",
					name: hasSelectedHotel.name,
					day: 1,
					nights: 2,
					image: hasSelectedHotel.image
				}})
			}
			setSelectedItem( hasSelectedHotel );
		}
		
		*/
	}
	

	//***************************
	//*** USER NOTIFICATIONS  ***
	//***************************



	const resetError = () => {
		return new Promise((resolve) => {
			setUserError(false);
			resolve();
		});
	}

	const formIsValid = () => {
		let errors = [];

		if( formData.createAccount && formData.consent == false ) {
			errors.push( 'We are unable to help with your enquiries unless you read and confirm our Privacy Notice.' );
		}


		if( !(formData.name != null && formData.name.length > 0) ){

		}

		if( !(formData.email != null && formData.email.length > 0) ){

		}

		if( !(formData.phone != null && formData.phone.length > 0 ) ){

		}

		if( !(formData.password != null && formData.password.length > 0 && formData.passwordValid ) ){

		}

		if( errors.length > 0 ){
			navigationDispatcher({type: "RAISE_ERROR", message: errors.pop(), code: null });
			return false;
		} else {
			return true;
		}
	}

	const processForm = () => {

		//we passs in sendPackage if logged in due to ascync state issues
		
		navigationDispatcher({
			type: 'TRACK_EVENT',
			action: 'event',
			category: 'form-submit',
			name: 'process-quote',
			trigger: 'web-js',
			data:  {
				pax: formData.pax,
				date: formData.date,
				activities: formData.activities.length,
				hotel: formData.hotel,
				hotelStandard: formData.hotelStandard,
				loggedIn: loggedIn,
				createAccount: formData.createAccount
			}
		});

		navigationDispatcher({type: "RESET_ERRORS" });

		if( formIsValid() ){

			navigationDispatcher({type: "START_PROCESSING" });

			const postData = new FormData();

			//fields from steps prior to submit
			postData.append( 'systemId', destination.systemId );
			postData.append( 'frmLocation', destination.id );
			postData.append( 'frmPax', formData.pax );
			postData.append( 'frmDate', formData.date );
			postData.append( 'frmNights', formData.nights );

			//Accommodation
			if( formData.hotel ){
				postData.append('frmHotel', JSON.stringify({
					id: formData.hotel.id,
					type: formData.hotel.type,
					pax: formData.hotel.pax,
					day: formData.hotel.day,
					nights: formData.hotel.nights
				}));
			} else {
				postData.append('frmHotel', false);
			}

			//package
			if( formData.package ) {
				postData.append( 'frmPackage', formData.package.id );
			} else {
				postData.append('frmPackage', false);
			}

			//superpackage
			if( formData.superPackage ) {
				postData.append('frmSuperPackage', JSON.stringify({
					id: formData.superPackage.id,
					pax: formData.superPackage.pax,
					day: formData.superPackage.day
				}));
			} else {
				postData.append('frmSuperPackage', false);
			}
			//activities
			if( formData.activities.length > 0 ) {
				formData.activities.map((activity) => {
					postData.append('frmEvents[]', JSON.stringify({
						id: activity.id,
						day: activity.day,
						pax: activity.pax,
						preferredTimeSlot: activity.preferredTimeSlot
					}));
				});
			} else {
				postData.append('frmEvents', false);
			}
			
			//survey
			if( formData.survey.completed ) {
				postData.append('frmSurvey', JSON.stringify({
					role: formData.survey.role,
					stage: formData.survey.stage
				}));
			} else {
				postData.append('frmSurvey', false);
			}

			if( loggedIn == false ) {
				//fields from submit
				postData.append( 'frmName', formData.name || '' );
				postData.append( 'frmEmail', formData.email );
				postData.append( 'frmPhone', formData.phone || '' );
				postData.append( 'frmCode', formData.code || '' );
				postData.append( 'frmConsent', formData.consent ? 1 : 0 );
				postData.append( 'frmMarketing', formData.marketing ? 1 : 0 );

				//Authentication
				postData.append( 'frmPassword', formData.password || '' );
				postData.append( 'frmCreateAccount', formData.createAccount ? 1 : 0 );
			}

			//unused fields
			postData.append( 'frmNotes', formData.notes );

			axios.post( '/quote/process', postData ).then( response => {

				if( response.status ){

					navigationDispatcher({
						type: 'TRACK_EVENT',
						action: 'event',
						category: 'success',
						name: 'quote-success',
						trigger: 'web-js',
						data: {
							id: response.data.id,
							canBookOnline: response.data.canBookOnline
						}
					});

					history.pushState(null, null, steps.form );

					//redirect
					window.location.replace( '/myaccount/success' );
				}

			}).catch( err => {
				const message = ( typeof err.response !== 'undefined' && err.response.data.error != null && err.response.data.error.length > 0 ) ? err.response.data.error : err.message;
				const code = ( typeof err.response !== 'undefined' && err.response.data.code != null && err.response.data.error.length > 0 ) ? err.response.data.code : null;
				navigationDispatcher({type: "RAISE_ERROR", message: message, code: code });

				navigationDispatcher({
					type: 'TRACK_EVENT',
					action: 'error',
					category: 'validation-errors',
					name: 'quote-submit',
					trigger: 'web-js',
					data:  {
						code: code,
						message: message
					}
				});

			}).then(()=>{
				navigationDispatcher({type: "STOP_PROCESSING" });
			});

		}
	}

	const clearAll = () => {
		for( const prop in defaultFormData){
			formDispatcher({type: "UPDATE_FIELD", key: prop, value: defaultFormData[prop]})
		}
	}
	
	return (
		<QuoteContext.Provider value={{ formData, formDispatcher, navigation, navigationDispatcher }}>

			<div>

				<ErrorMessage 
					navigation={navigation} 
					navigationDispatcher={navigationDispatcher}
					context="global"
				/>

				{ loggedIn ? (
          
				<div className="full-width-block bg-dark-stag-light-hen pd-v-md">
					<div className="row no-padding">
						<div className="pd-h-tch pd-h-dsk-md fill">
							<div className="align-content">
								<div className="grid-item small-half">
									{/* <Heading hasBookings={hasBookings} /> */}
									<h1 className="v2 white no-mg-b">Build your {destination.name} {navigation.tag}</h1>
								</div>
								<div className="grid-item small-half">
									<Progress />
								</div>
							</div>
						</div>
					</div>
				</div>

			) : (

			<div className="full-width-block bg-dark-stag-light-hen pd-v-md">
				<div className="row no-padding">
					<div className="pd-h-tch pd-h-dsk-md fill">
						<div className="align-content">
							<div className="grid-item small-half">
								<h1 className="v2 white no-mg-b">Build your {destination.name} {destination.siteId == 1 ? 'Stag' : 'Hen'}</h1>
							</div>
							<div className="grid-item small-half">
								<Progress />
							</div>
						</div>
					</div>
				</div>
			</div>
			)}

				{selectedItem ? (
					<div className="full-width-block bg-green pd-v text-center animated fadeIn">
						<div className="row">
							<div className="column">
								<div className="align-content vertical horizontal center">
									<span className="icon icon-tick white mg-r"></span> We've added <span className="text-medium white bold mg-h-sm">{selectedItem.name}</span> to your quote!
								</div>
							</div>
						</div>
					</div>
				) : null}

				{loading && data !== null ? (
					<Loader />
				) : (
					<div>
						<Router primary={false}>

							<Itinerary
								default
								path={steps.itinerary}
								processForm={processForm}
								hasPackages={destination.hasPackages}
								defaultPackages={elements.packages}
								siteId={destination.siteId}
								edit={false}
							/>

							<Activities
								path={steps.activities}
								siteId={destination.siteId}
								defaultActivities={elements.events}
								eventTags={elements.eventTags}
								siteTags={elements.siteTags}
								/>

							<Accommodation
								path={steps.accommodation}
								coords={elements.coords}
								accommodation={elements.accommodation}
								selectedHotel={formData.hotel}
								allowPick={elements.hotelStandards.length > 0 ? true : false}
								allowedFilters={elements.hotelStandardFilter}
								processForm={processForm}
							/>
							<AccommodationTypes
								path={steps.accommodationTypes}
								selectedHotel={formData.hotelStandard}
								types={elements.hotelStandards}
								processForm={processForm}
							/>

							<SuperPackages
								path={steps.superPackages}
								locationId={navigation.destination.id}
								selectedSuperPackage={formData.superPackage}
							/>

							<Submit
								path={steps.submit}
								locationImage={elements.image}
								processForm={processForm}
								resetError={resetError}
								siteId={destination.siteId}
							/>
						</Router>


						<Modal
							isModalOpen={navigation.modal.open}
							type={navigation.modal.type}
							payload={navigation.modal.payload}
							title={navigation.modal.title}
							close={() => { navigationDispatcher({ type: 'CLOSE_MODAL' })}}
						/>

					</div>
				)}
			</div>
		</QuoteContext.Provider>
	)

}

export default QuotebuilderV3;
